import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiMap, FiTarget, FiTrophy } = FiIcons;

const HowItWorksSection = () => {
  const steps = [
    {
      number: 1,
      icon: FiSearch,
      title: "Elite Performance Assessment",
      description: "We analyze which executive functions are limiting your competitive edge. Is it decision speed? Attention control? Pressure management?",
      color: "accent-magenta"
    },
    {
      number: 2,
      icon: FiMap,
      title: "Your Personalized Champion Blueprint", 
      description: "Get a customized training plan targeting your specific cognitive performance barriers. This is precision mental training for elite athletes.",
      color: "accent-gold"
    },
    {
      number: 3,
      icon: FiTarget,
      title: "Mental Skills Training Sessions",
      description: "Weekly 1-on-1 coaching/therapy to build and strengthen your cognitive performance toolkit. Practice pressure scenarios, master decision-making drills, level up your mental game.",
      color: "accent-purple"
    },
    {
      number: 4,
      icon: FiTrophy,
      title: "Dominate Like a Champion",
      description: "Apply your enhanced executive functions in competition with ongoing support, ensuring these mental skills become your secret weapon.",
      color: "accent-lavender"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            From Good Athlete to Game-Changer in 4 Steps
          </h2>
        </motion.div>

        {/* Steps */}
        <div className="space-y-12">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="relative"
            >
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 bg-${step.color} text-white rounded-full flex items-center justify-center font-bold text-xl`}>
                      {step.number}
                    </div>
                    <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                  
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Visual */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className={`bg-gradient-to-br from-${step.color}/10 to-${step.color}/5 p-12 rounded-2xl border border-${step.color}/20 text-center`}>
                    <div className={`inline-flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg mb-6`}>
                      <SafeIcon icon={step.icon} className={`h-12 w-12 text-${step.color}`} />
                    </div>
                    
                    <div className={`text-6xl font-bold text-${step.color} mb-4`}>
                      {step.number}
                    </div>
                    
                    <div className={`w-16 h-1 bg-${step.color} mx-auto rounded-full`}></div>
                  </div>
                </div>
              </div>

              {/* Connecting line (except for last step) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 mt-8">
                  <div className="w-1 h-12 bg-gradient-to-b from-gray-300 to-gray-200 rounded-full"></div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-accent-light/50 to-accent-purple/30 p-8 lg:p-12 rounded-2xl border border-accent-lavender/30">
            <p className="text-xl text-gray-800 mb-8 leading-relaxed max-w-3xl mx-auto">
              Ready to transform from a good athlete into a game-changer? Your journey to mental dominance starts with step one.
            </p>
            
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary text-lg"
            >
              Start Your Assessment
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;